import { GridColDef, GridRowClassNameParams } from "@mui/x-data-grid";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import { useTranslation } from "react-i18next";
import { Construction, LocationOn } from "@mui/icons-material";
import {
  JOBS_ENDPOINT,
  JOB_STATUS_OPTIONS,
  JOB_STATUS_COLOR_MAP,
  JOB_KIND_OPTIONS,
  getJobKindChipSx,
} from "../../../consts";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useAuthContext,
  useHasPermission,
  useHasAnyPermission,
  useTenantName,
} from "../../../contexts/auth";
import useGeolocation from "../../../hooks/useGeolocation";
import { JOB_MESSAGES_UPDATED_EVENT } from "./viewJob/viewJob.component";
import GridAddressCell from "../../../components/ui/gridAddressCell/gridAddressCell.component";
import { formatDateTime, formatDateTimeInline, getContrastColor } from "../../../utils";
import { useMemo } from "react";
import RelevantDateCell, {
  DateTimeLines,
} from "../../../components/service/relevantDateCell/relevantDateCell.component";
import {
  containsStringFilterOperators,
  dateRangeFilterOperators,
  equalsNumberFilterOperators,
  singleSelectFilterOperators,
} from "../../../components/ui/customDataGrid/filterOperators";

// Row-level markers for the jobs grid. They live on the row (not inside the
// "Number" cell) so they stay visible even when the user hides the Number
// column: a complaint job keeps its red accent, and a job with unread messages
// stays highlighted, regardless of which columns are shown.
const COMPLAINT_ROW_CLASS = "jobs-list-row--complaint";
const UNREAD_ROW_CLASS = "jobs-list-row--unread";
const SUGGESTED_ROW_CLASS = "jobs-list-row--suggested";
const JOB_ROW_MARKER_SX = {
  [`& .MuiDataGrid-row.${COMPLAINT_ROW_CLASS}`]: {
    boxShadow: "inset 4px 0 0 0 #d32f2f",
    backgroundColor: "rgba(211, 47, 47, 0.06)",
  },
  [`& .MuiDataGrid-row.${COMPLAINT_ROW_CLASS}:hover`]: {
    backgroundColor: "rgba(211, 47, 47, 0.12)",
  },
  [`& .MuiDataGrid-row.${UNREAD_ROW_CLASS}`]: {
    backgroundColor: "rgba(25, 118, 210, 0.08)",
  },
  [`& .MuiDataGrid-row.${UNREAD_ROW_CLASS}:hover`]: {
    backgroundColor: "rgba(25, 118, 210, 0.14)",
  },
  [`& .MuiDataGrid-row.${UNREAD_ROW_CLASS} .MuiDataGrid-cell`]: {
    fontWeight: 700,
  },
  [`& .MuiDataGrid-row.${COMPLAINT_ROW_CLASS}.${UNREAD_ROW_CLASS}`]: {
    backgroundColor: "rgba(211, 47, 47, 0.08)",
  },
  [`& .MuiDataGrid-row.${SUGGESTED_ROW_CLASS}`]: {
    boxShadow: "inset 4px 0 0 0 #2e7d32",
    backgroundColor: "rgba(46, 125, 50, 0.06)",
  },
  [`& .MuiDataGrid-row.${SUGGESTED_ROW_CLASS}:hover`]: {
    backgroundColor: "rgba(46, 125, 50, 0.12)",
  },
  [`& .MuiDataGrid-row.${SUGGESTED_ROW_CLASS}.${COMPLAINT_ROW_CLASS}`]: {
    backgroundColor: "rgba(211, 47, 47, 0.08)",
  },
  [`& .MuiDataGrid-row.${SUGGESTED_ROW_CLASS}.${UNREAD_ROW_CLASS}`]: {
    backgroundColor: "rgba(46, 125, 50, 0.1)",
  },
};

type AssigneeDistance = {
  distance_km: number;
  // "live" - the browser's position on the "my jobs" view; "gps" - the last
  // fix the person's browser stored; "job" - the address of the job they last
  // moved to "In progress" (job_number), for someone who doesn't share their
  // location. recorded_at is when the fix was taken / the job was started.
  source: "live" | "gps" | "job";
  job_number: string | null;
  recorded_at: string | null;
};

export default function JobsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // Equipment with no installation address is back with us rather than at the
  // client's site, so the workspace's own name stands in for the address.
  const tenantName = useTenantName();
  const statusParam = params.get("status") || "";
  const kindParam = params.get("kind") || "";
  const assignedToParam = params.get("assigned_to") || "";
  const { currentUser } = useAuthContext();
  // The browser's live position is only requested on the "my jobs" view, for
  // the Distance column and the suggestion (without it the backend uses the
  // technician's last known position). Filtering by someone else instead
  // shows their distance on their "Assigned To" chip, measured from their
  // last stored fix or the job they last started (backend-gated by the
  // technicians-map permission).
  // currentUser.id comes back from the API as a number despite
  // its string type - see jobsListActions.component.tsx's String(currentUser.id)
  // for the same coercion.
  const isOwnJobsView =
    Boolean(assignedToParam) && currentUser != null && assignedToParam === String(currentUser.id);
  const { coords } = useGeolocation(isOwnJobsView);
  const extraParams = useMemo(
    () => ({
      assigned_to: assignedToParam || undefined,
      lat: coords?.lat,
      lng: coords?.lng,
    }),
    [assignedToParam, coords]
  );
  const initialFilterModel = useMemo(() => {
    const items = [];
    if (statusParam) {
      items.push({ id: "status-filter", field: "status", operator: "is", value: statusParam });
    }
    if (kindParam) {
      items.push({ id: "kind-filter", field: "kind", operator: "is", value: kindParam });
    }
    return items.length > 0 ? { items } : undefined;
  }, [statusParam, kindParam]);
  const canViewJobs = useHasAnyPermission(["service.view_job", "service.view_own_job"]);
  const canViewMessages = useHasPermission("messaging.view_message");

  const STATUS_OPTIONS = useMemo(
    () => JOB_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const KIND_OPTIONS = useMemo(
    () => JOB_KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const COMPLAINT_KIND = "C";

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        filterOperators: equalsNumberFilterOperators(),
      },
      {
        field: "number",
        headerName: t("Number"),
        filterOperators: containsStringFilterOperators(),
        renderCell: (params) => {
          const number = params.value || "-";
          // Complaint jobs and jobs with unread messages are marked on the row
          // itself (see getRowClassName / JOB_ROW_MARKER_SX), not here - the
          // Number column can be hidden by the user.
          const kindChipSx = getJobKindChipSx(params.row.kind);
          const suggestedIcon = params.row.is_suggested_next ? (
            <Tooltip title={t("Suggested as closest")}>
              <LocationOn fontSize="small" sx={{ color: "#2e7d32" }} />
            </Tooltip>
          ) : null;

          const numberContent = kindChipSx ? (
            <Chip label={number} size="small" sx={kindChipSx} />
          ) : (
            <Typography component="span" variant="body2">
              {number}
            </Typography>
          );

          if (!suggestedIcon) {
            return numberContent;
          }

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {numberContent}
              {suggestedIcon}
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: t("Status"),
        filterable: true,
        type: "singleSelect",
        valueOptions: STATUS_OPTIONS,
        filterOperators: singleSelectFilterOperators(),
        valueGetter: (value, row) => row.status,
        renderCell: (params) => {
          const status = params.row.status;
          const label = params.row.status_display || params.value || "-";
          return (
            <Chip
              label={label}
              color={JOB_STATUS_COLOR_MAP[status] ?? "default"}
              size="small"
            />
          );
        },
      },
      {
        // Whether a live invoice already bills this job - what the "still to
        // invoice" view of the list is built on. Hidden by default: it only
        // means anything to workspaces that bill their service work.
        field: "is_invoiced",
        headerName: t("Invoiced"),
        filterable: true,
        type: "boolean",
        valueGetter: (value, row) => Boolean(row.is_invoiced),
      },
      {
        // Not shown as a column (kept out of the grid via defaultHiddenFields below) -
        // exists only so "kind" is a valid filterable field in the grid's own Filters
        // panel, without duplicating the kind badge already shown in the Number column.
        field: "kind",
        headerName: t("Kind"),
        filterable: true,
        type: "singleSelect",
        valueOptions: KIND_OPTIONS,
        filterOperators: singleSelectFilterOperators(),
        valueGetter: (value, row) => row.kind,
      },
      {
        field: "created_at",
        headerName: t("Created at"),
        filterOperators: dateRangeFilterOperators(),
        renderCell: (params) => {
          if (!params.value) return "-";
          const [date, time] = formatDateTime(params.value).split("\n");
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <DateTimeLines date={date} time={time} />
            </Box>
          );
        },
      },
      // Only on the "my jobs" view. Measured from the browser's position when
      // it's shared; otherwise the backend falls back to the technician's
      // last known position - a stored fix or the job they last started
      // (JobViewSet._own_origin) - and rows show "-" if there's neither.
      ...(isOwnJobsView
        ? [
            {
              field: "distance_km",
              headerName: t("Distance (km)"),
              description: coords
                ? t("Distance from your current location")
                : t("Measured from your last known location or the job you last started"),
              sortable: true,
              filterable: false,
              valueFormatter: (value: number | null) =>
                typeof value === "number" ? `${value.toFixed(1)} km` : "-",
            },
          ]
        : []),
      {
        field: "timeline_date",
        headerName: t("Relevant date"),
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <RelevantDateCell value={params.value} status={params.row.status} />
        ),
      },
      {
        field: "short_name",
        headerName: t("Client"),
        sortable: false,
        filterOperators: containsStringFilterOperators(),
        valueGetter: (value, row) => row.short_name || "-",
      },
      {
        field: "equipment_names",
        headerName: t("Equipments"),
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const equipmentNames = Array.isArray(params.row.equipment_names) ? params.row.equipment_names : [];
          const equipmentAddresses = Array.isArray(params.row.equipment_addresses) ? params.row.equipment_addresses : [];

          if (equipmentNames.length === 0) {
            return "-";
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.5,
                width: "100%",
                py: 0.5,
              }}
            >
              {equipmentNames.map((name: string, index: number) => {
                const address = equipmentAddresses[index] || "";

                return (
                  <Box key={`${name}-${index}`}>
                    <Typography variant="body2" sx={{ lineHeight: 1.15 }}>
                      {name || "-"}
                    </Typography>
                    <GridAddressCell addressText={address} fallbackText={tenantName} singleLine />
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
      {
        field: "list_issues",
        headerName: t("Issues"),
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const items = params.row.list_issues;
          if (!items || !Array.isArray(items) || items.length === 0) return "-";
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                width: "100%",
                height: "100%",
                minHeight: 52,
              }}
            >
              {items.map((item: { title?: string; code?: string | null; color?: string | null }, index: number) => {
                const title = item.title || "-";
                const label = item.code ? `${title} (${item.code})` : title;
                return (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    sx={
                      item.color
                        ? {
                            bgcolor: item.color,
                            color: getContrastColor(item.color),
                            "& .MuiChip-label": { color: getContrastColor(item.color) },
                          }
                        : {
                            bgcolor: "rgba(128, 128, 128, 0.25)",
                            color: "text.secondary",
                          }
                    }
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        field: "list_services",
        headerName: t("Services"),
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const items = params.row.list_services;
          if (!items || !Array.isArray(items) || items.length === 0) return "-";
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                width: "100%",
                height: "100%",
                minHeight: 52,
              }}
            >
              {items.map((item: { title?: string; color?: string | null }, index: number) => {
                const title = item.title || "-";
                return (
                  <Chip
                    key={index}
                    label={title}
                    size="small"
                    sx={
                      item.color
                        ? {
                            bgcolor: item.color,
                            color: getContrastColor(item.color),
                            "& .MuiChip-label": { color: getContrastColor(item.color) },
                          }
                        : {
                            bgcolor: "rgba(128, 128, 128, 0.25)",
                            color: "text.secondary",
                          }
                    }
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        field: "assigned_to_names",
        headerName: t("Assigned To"),
        sortable: false,
        filterOperators: containsStringFilterOperators(),
        valueGetter: (_value, row) => {
          const names = row.assigned_to_names;
          if (Array.isArray(names) && names.length > 0) {
            return names.filter(Boolean).join(", ");
          }
          return "-";
        },
        renderCell: (params) => {
          const names = params.row.assigned_to_names;
          if (!Array.isArray(names) || names.length === 0) {
            return "-";
          }
          // assigned_to_distances is aligned index-by-index with the names,
          // so pair them up before dropping empty names. Only the people the
          // list is filtered by get a distance (from their last known
          // position) - see JobViewSet._assignee_origins.
          const distances: (AssigneeDistance | null)[] = params.row.assigned_to_distances ?? [];
          const assignees = names
            .map((name: string, index: number) => ({ name, distance: distances[index] ?? null }))
            .filter((assignee: { name: string }) => Boolean(assignee.name));
          if (assignees.length === 0) {
            return "-";
          }
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                width: "100%",
                height: "100%",
                minHeight: 52,
              }}
            >
              {assignees.map(({ name, distance }, index: number) => {
                if (!distance) {
                  return (
                    <Chip key={`${name}-${index}`} label={name} size="small" variant="outlined" />
                  );
                }
                const time = distance.recorded_at ? formatDateTimeInline(distance.recorded_at) : "";
                const tooltip =
                  distance.source === "live"
                    ? t("Distance from your current location")
                    : distance.source === "job"
                      ? t("Distance from the address of job {{number}} (started {{time}})", {
                          number: distance.job_number,
                          time,
                        })
                      : t("Distance from the last known location ({{time}})", { time });
                return (
                  <Tooltip key={`${name}-${index}`} title={tooltip}>
                    <Chip
                      icon={
                        distance.source === "job" ? (
                          <Construction fontSize="small" />
                        ) : (
                          <LocationOn fontSize="small" />
                        )
                      }
                      label={`${name} · ${distance.distance_km.toFixed(1)} km`}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                );
              })}
            </Box>
          );
        },
      },
    ],
    [STATUS_OPTIONS, KIND_OPTIONS, t, tenantName, coords, isOwnJobsView]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    const classNames: string[] = [];
    if (params.row.kind === COMPLAINT_KIND) {
      classNames.push(COMPLAINT_ROW_CLASS);
    }
    if (
      canViewMessages &&
      (Number(params.row.unread_messages_count) || 0) > 0
    ) {
      classNames.push(UNREAD_ROW_CLASS);
    }
    if (params.row.is_suggested_next) {
      classNames.push(SUGGESTED_ROW_CLASS);
    }
    return classNames.join(" ");
  };

  const handleRowClick = (id: string) => {
    navigate(`/service/jobs/${id}`);
  };

  if (!canViewJobs) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<Construction />}
      title={t("Jobs list")}
      url={JOBS_ENDPOINT}
      gridSettingsKey="SERVICE_JOBS"
      initialFilterModel={initialFilterModel}
      extraParams={extraParams}
      columns={columns}
      defaultHiddenFields={["kind", "is_invoiced"]}
      dataGridProps={{
        getRowHeight: () => "auto",
        getRowClassName,
        sx: JOB_ROW_MARKER_SX,
      }}
      refreshEventName={["jobAdded", JOB_MESSAGES_UPDATED_EVENT]}
      onRowClick={handleRowClick}
    />
  );
}
